import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useAppAuth } from './useAppAuth';

const DEVELOP_MODE_ROLES = ['admin', 'builder'];

function useCanUseDraftMode() {
  const { user } = useAppAuth();
  return useMemo(() => {
    if (!user) return false;
    const roles = user.roles || [];
    return roles.some((r: any) => {
      const key = (typeof r === 'string' ? r : r?.key || r?.name || '').toLowerCase();
      return DEVELOP_MODE_ROLES.includes(key);
    });
  }, [user]);
}

/**
 * Hook to get the current draft version ID.
 * If this returns a value, the user is in draft/develop mode.
 * Roles that cannot use draft mode are forced back to production.
 */
export function useDraftVersionId() {
  const canUseDraft = useCanUseDraftMode();

  return useQuery<string | null>({
    queryKey: ['draft_version_id'],
    queryFn: async () => {
      const stored = localStorage.getItem('draft_version_id');
      if (stored && !canUseDraft) {
        // User's role doesn't allow draft mode — clear stale state
        localStorage.removeItem('draft_version_id');
        return null;
      }
      return stored || null;
    },
    staleTime: 30 * 60 * 1000, // 30 minutes
  });
}

/**
 * Hook to check if the user is currently in draft/develop mode.
 */
export function useIsInDraftMode() {
  const { data: draftVersionId } = useDraftVersionId();
  return !!draftVersionId;
}

/**
 * Hook to toggle between draft/develop mode and production mode.
 */
export function useDraftModeToggle() {
  const { data: draftVersionId } = useDraftVersionId();
  const queryClient = useQueryClient();
  
  return useCallback(async (trigger: string = 'unknown') => {
    if (draftVersionId) {
      // Currently in draft mode, exit to production
      localStorage.removeItem('draft_version_id');
      queryClient.setQueryData(['draft_version_id'], null);
      // Invalidate queries to refresh data
      queryClient.invalidateQueries();
    } else {
      // Currently in production, enter draft mode
      const draft_version_id = 'draft_mode';
      localStorage.setItem('draft_version_id', draft_version_id);
      queryClient.setQueryData(['draft_version_id'], draft_version_id);
      // Invalidate queries to refresh data
      queryClient.invalidateQueries();
    }
  }, [draftVersionId, queryClient]);
}

